/*
 * Description:
 * ---------------------
 * Investigations: the Agent Mesh's tasks (server/pkg/meshctl). Someone asks
 * how a site is doing, the console's server asks the site's box, the box asks
 * its Weavian gateway, and the answer comes back signed at every step. The
 * screen answers first in the server's own sentences, keeps the trace of who
 * asked whom behind one disclosure, and offers the signed record to download.
 * Outside agents' questions (OutsideAgentsPanel) show here beside a person's.
 * All wording is in meshWords.js.
 *
 * ! file components/InvestigationsPanel.js
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiAlertTriangle } from "react-icons/fi";
import useVisibleInterval from "../hooks/useVisibleInterval";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { isAnchorNode, isMobileNode } from "../utils/nodeKind";
import * as W from "./meshWords";
import "./Firewall.css";
import "./Mesh.css";

const LIST_MS = 10000;
const TASK_MS = 2000;

/* One read or change in a trace, and the ones it made beneath it; the
   Changes screen draws its traces with it too. */
export function Hop({ hop, ctl }) {
  const answer = W.hopAnswer(hop, ctl);
  return (
    <li className="mesh-hop">
      <div>{W.hopAsked(hop, ctl)}</div>
      <div className={`mesh-answer is-${answer.tone}`}><span className="mesh-dot" aria-hidden="true" /> {answer.text}</div>
      {answer.why && <div className="fw-muted mesh-why">{answer.why}</div>}
      {hop.result && (
        <details className="mesh-raw">
          <summary>What it answered</summary>
          <pre>{JSON.stringify(hop.result, null, 2)}</pre>
        </details>
      )}
      {hop.hops?.length > 0 && <ol className="mesh-hops">{hop.hops.map((h, i) => <Hop key={i} hop={h} ctl={ctl} />)}</ol>}
    </li>
  );
}

/* latestInterruption is the newest interruption a site check read, to look
 * into next. */
const latestInterruption = (task) => {
  const stalls = task.hops?.find((h) => h.capability === "flow_stalls" && h.status === "ok");
  return stalls?.result?.episodes?.[0]?.id || "";
};

function Task({ task, sealed, ctl, serverApiUrl, start, busy }) {
  const st = W.status(task);
  const took = W.took(task);
  const next = task.kind === "site" && task.status === "done" ? latestInterruption(task) : "";
  return (
    <section className="fw-card" aria-live="polite">
      <h3 className="fw-card-title">{task.question}</h3>
      <p className="fw-muted">
        Asked by {W.by(task.by)} on {W.when(task.started)}{took ? `, answered in ${took}` : ""}.
      </p>
      {task.status === "running" && <p className="fw-lead"><span className={`mesh-answer is-${st.tone}`}><span className="mesh-dot" aria-hidden="true" /> {st.text}…</span></p>}
      {task.findings?.length > 0 && <ul className="mesh-findings">{task.findings.map((f, i) => <li key={i}>{f}</li>)}</ul>}
      {next && (
        <button type="button" className="fw-btn" disabled={busy} onClick={() => start({ kind: "interruption", site: task.sites[0], episode: next })}>
          {W.LOOK_INTO}
        </button>
      )}
      {task.hops?.length > 0 && (
        <details className="mesh-how">
          <summary>{W.HOW_WE_KNOW}</summary>
          <p className="fw-muted">{W.howWeKnow(W.checkedCount(task.hops))}</p>
          <ol className="mesh-hops">{task.hops.map((h, i) => <Hop key={i} hop={h} ctl={ctl?.node} />)}</ol>
        </details>
      )}
      {sealed
        ? (
          <div className="mesh-record">
            <a className="fw-btn" href={serverApiUrl(`/mesh/tasks/${encodeURIComponent(task.id)}/export`)} download>{W.SIGNED_RECORD}</a>
            <p className="fw-muted">{W.signedRecord(ctl)}</p>
          </div>
        )
        : task.status === "running" && <p className="fw-muted">{W.NOT_SEALED}</p>}
    </section>
  );
}

export default function InvestigationsPanel({ serverApiUrl }) {
  const [tasks, setTasks] = useState(null);
  const [sites, setSites] = useState([]);
  const [site, setSite] = useState("");
  const [ctl, setCtl] = useState(null);
  const [picked, setPicked] = useState("");
  const [open, setOpen] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const get = useCallback(async (path) => {
    const res = await fetchWithTimeout(serverApiUrl(path), { credentials: "same-origin" }).catch(() => null);
    if (!res) throw new Error("The Weavian server did not answer.");
    if (res.status === 404 && path === "/mesh/tasks") throw new Error(W.NOT_ON_SERVER);
    const out = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(out.error || `The server refused (HTTP ${res.status}).`);
    return out;
  }, [serverApiUrl]);

  const readList = useCallback(async () => {
    try {
      const out = await get("/mesh/tasks?limit=50");
      if (!mounted.current) return;
      setTasks(out.tasks || []);
      setError("");
    } catch (e) {
      if (mounted.current) setError(e.message);
    }
  }, [get]);
  useVisibleInterval(readList, LIST_MS);

  const readOpen = useCallback(async () => {
    if (!picked) return;
    try {
      const out = await get(`/mesh/tasks/${encodeURIComponent(picked)}`);
      if (mounted.current) setOpen(out);
    } catch (e) {
      if (mounted.current) setError(e.message);
    }
  }, [get, picked]);
  useVisibleInterval(readOpen, open?.task?.status === "running" ? TASK_MS : LIST_MS);
  useEffect(() => { readOpen(); }, [readOpen]);

  useEffect(() => {
    get("/mesh/controller").then((c) => mounted.current && setCtl(c)).catch(() => {});
    get("/nodes").then((out) => {
      if (!mounted.current) return;
      const boxes = (out.nodes || out || []).filter((n) => !isAnchorNode(n) && !isMobileNode(n))
        .sort((a, b) => a.node_id.localeCompare(b.node_id));
      setSites(boxes);
      setSite((s) => s || boxes.find((n) => !n.stale)?.node_id || boxes[0]?.node_id || "");
    }).catch(() => {});
  }, [get]);

  const start = async (req) => {
    setBusy(true);
    const res = await fetchWithTimeout(serverApiUrl("/mesh/tasks"), {
      method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify(req),
    }, 15000).catch(() => null);
    const out = res ? await res.json().catch(() => ({})) : {};
    if (!mounted.current) return;
    setBusy(false);
    if (!res?.ok) {
      setError(out.error || "The Weavian server did not answer, so nothing was asked.");
      return;
    }
    setError("");
    setOpen({ task: out, sealed: false });
    setPicked(out.id);
    readList();
  };

  const customer = sites.find((n) => n.node_id === site)?.domain || "";
  return (
    <div className="fw">
      {error && <div className="fw-notice is-bad" role="alert"><FiAlertTriangle size={16} /> <span>{error}</span></div>}
      <section className="fw-card">
        <p className="fw-lead">{W.LEAD}</p>
        {sites.length === 0
          ? <p className="fw-muted">{W.NO_SITES}</p>
          : (
            <div className="mesh-ask">
              <label>{W.ASK}{" "}
                <select className="fw-input" value={site} onChange={(e) => setSite(e.target.value)} aria-label="Site">
                  {sites.map((n) => <option key={n.node_id} value={n.node_id}>{n.node_id}{n.stale ? " (offline)" : ""}</option>)}
                </select>
              </label>
              <button type="button" className="fw-btn is-primary" disabled={busy || !site} onClick={() => start({ kind: "site", site })}>{W.CHECK_SITE}</button>
              <button type="button" className="fw-btn" disabled={busy || !site} onClick={() => start({ kind: "changes", site })}>{W.CHECK_CHANGES}</button>
              <button type="button" className="fw-btn" disabled={busy || !customer} onClick={() => start({ kind: "fleet", customer })}>{W.CHECK_ALL}</button>
            </div>
          )}
      </section>
      {open?.task && <Task task={open.task} sealed={open.sealed} ctl={ctl} serverApiUrl={serverApiUrl} start={start} busy={busy} />}
      <section className="fw-card">
        <h3 className="fw-card-title">{W.RECENT}</h3>
        {tasks === null && !error && <p className="fw-muted">Reading…</p>}
        {tasks?.length === 0 && <p className="fw-muted">{W.NO_TASKS}</p>}
        {tasks?.length > 0 && (
          <ul className="mesh-tasks">
            {tasks.map((t) => {
              const st = W.status(t);
              return (
                <li key={t.id}>
                  <button type="button" className={`mesh-task${t.id === picked ? " is-picked" : ""}`} onClick={() => setPicked(t.id)}>
                    <span className="mesh-task-q">{t.question}</span>
                    <span className="fw-muted">{W.by(t.by)} · {W.when(t.started)}</span>
                    <span className={`mesh-answer is-${st.tone}`}><span className="mesh-dot" aria-hidden="true" /> {st.text}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
